import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import {
  JoinPayload,
  joinAsPatient,
  leaveSessionPublic,
  previewPatientTeleconsult,
} from '../../services/teleconsultService';
import TeleconsultaRoom from './TeleconsultaRoom';

/**
 * Rota pública /teleconsulta/p/:token
 *
 * Fluxo:
 *  1. GET preview -> mostra horário e valida token.
 *  2. Paciente digita o próprio nome e clica em "Entrar na sala".
 *  3. POST /join -> recebe JWT e abre a sala.
 *
 * Trata:
 *  - 425 (cedo demais) -> mostra contagem regressiva
 *  - 410 (expirado/indisponível) -> mensagem clara
 */
const PatientTeleconsultPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState('');
  const [appointmentInfo, setAppointmentInfo] = useState<{
    date: string;
    startTime: string;
    durationMinutes: number;
  } | null>(null);
  const [name, setName] = useState('');
  const [joining, setJoining] = useState(false);
  const [join, setJoin] = useState<JoinPayload | null>(null);

  // -------- Preview --------
  const loadPreview = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setPreviewError('');
    try {
      const data = await previewPatientTeleconsult(token);
      setAppointmentInfo(data.appointment);
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Não foi possível abrir o link da teleconsulta.';
      setPreviewError(msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  // -------- Entrar --------
  const handleJoin = async () => {
    if (!token) return;
    if (!name.trim()) {
      setPreviewError('Informe seu nome para entrar.');
      return;
    }
    setJoining(true);
    setPreviewError('');
    try {
      const data = await joinAsPatient(token, name.trim());
      setJoin(data);
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Não foi possível entrar na sala.';
      setPreviewError(msg);
    } finally {
      setJoining(false);
    }
  };

  // -------- Saída -> ping no backend --------
  const handleLeft = useCallback(() => {
    if (join?.sessionId) {
      leaveSessionPublic(join.sessionId);
    }
  }, [join]);

  // -------- Já dentro da sala --------
  if (join) {
    return (
      <TeleconsultaRoom
        join={join}
        participantRole="patient"
        title="Teleconsulta"
        subtitle={
          appointmentInfo
            ? `Consulta de ${formatDate(appointmentInfo.date)} às ${appointmentInfo.startTime.slice(
                0,
                5
              )}`
            : undefined
        }
        afterLeftMessage="Sua consulta foi encerrada. Em caso de dúvidas, fale com a clínica pelo WhatsApp."
        onLeft={handleLeft}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f1f5f9',
        p: 2,
      }}
    >
      <Card
        elevation={6}
        sx={{
          maxWidth: 480,
          width: '100%',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
            color: '#fff',
            px: 3,
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <VideoCallIcon sx={{ fontSize: 36 }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
              Sua teleconsulta
            </Typography>
            <Typography sx={{ opacity: 0.85, fontSize: '0.85rem' }}>
              Atendimento online por vídeo
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: 3 }}>
          {loading && (
            <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
              <CircularProgress sx={{ color: '#14B8A6' }} />
              <Typography color="text.secondary">Carregando…</Typography>
            </Stack>
          )}

          {!loading && previewError && (
            <Stack spacing={2}>
              <Alert severity="warning">{previewError}</Alert>
              {appointmentInfo && (
                <Typography variant="body2" color="text.secondary">
                  Consulta agendada: <strong>{formatDate(appointmentInfo.date)}</strong>{' '}
                  às <strong>{appointmentInfo.startTime.slice(0, 5)}</strong>
                </Typography>
              )}
              <Button onClick={loadPreview} variant="outlined">
                Tentar novamente
              </Button>
            </Stack>
          )}

          {!loading && !previewError && appointmentInfo && (
            <Stack spacing={2}>
              <Box>
                <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  Sua consulta
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  {formatDate(appointmentInfo.date)} —{' '}
                  {appointmentInfo.startTime.slice(0, 5)}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  Duração estimada: {appointmentInfo.durationMinutes} min
                </Typography>
              </Box>

              <TextField
                label="Seu nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                autoFocus
                disabled={joining}
              />

              <Button
                variant="contained"
                size="large"
                onClick={handleJoin}
                disabled={joining || !name.trim()}
                startIcon={joining ? <CircularProgress size={20} color="inherit" /> : <VideoCallIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1.25,
                }}
              >
                {joining ? 'Conectando…' : 'Entrar na sala'}
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                Você precisará permitir acesso à câmera e ao microfone. Esta consulta pode ser gravada
                pela clínica (áudio da sala, profissional e paciente) para registro e transcrição no prontuário,
                conforme a política da instituição.
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

function formatDate(dateStr: string): string {
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export default PatientTeleconsultPage;
